import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LeaveCall from "../../assets/svg/leaveCall.svg";
import MuteSpeakers from "../../assets/svg/MuteSpeakers.svg";
import RaisHand from "../../assets/svg/RaisHand.svg";
import ScreenShare from "../../assets/svg/ScreenShare.svg";
import ToggleCam from "../../assets/svg/ToggleCam.svg";
import ToggleMic from "../../assets/svg/ToggleMic.svg";
import { useCall, useCallStateHooks } from "@stream-io/video-react-sdk";
import ConnectionQualityIndicator from "./ConnectionQualityIndicator";
import { confirmDialog } from "../Sweetalert/Sweetalert";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { useDispatch, useSelector } from "react-redux";
import { clearLive } from "../../features/live/liveSlice";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import Loader from "./Loader";

const CallControls = ({
  displayButtons,
  handleRecordingStopped,
}: {
  displayButtons: any;
  handleRecordingStopped: any;
}) => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const call = useCall();
  const user = useSelector(selectUserInformations);
  const {
    useMicrophoneState,
    useCameraState,
    useScreenShareState,
    useHasOngoingScreenShare,
    useLocalParticipant,
    useSpeakerState,
  } = useCallStateHooks();

  const { microphone, isMute: isAudioDisabled } = useMicrophoneState();
  const { camera, isMute: isCamDisabled } = useCameraState();
  const { screenShare, isMute: isScreenSharing } = useScreenShareState();
  const { speaker } = useSpeakerState();
  const isSomeoneScreenSharing = useHasOngoingScreenShare();
  const localParticipant = useLocalParticipant();
  const dispatch = useDispatch();
  const [isMuted, setIsMuted] = useState(false);
  const [toggleRaisehand, setToggleRaisehand] = useState(false);

  const handleEndCallAPI = useCallback(
    async (retries = 3): Promise<void> => {
      try {
        const { data } = await axios.put(
          `${API.Courses.app_courses_live_endLive}/${call?.cid}`,
          { notes: "Call ended" },
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        if (data.status !== 200) throw new Error(data.message);
        dispatch(clearLive());
      } catch (error) {
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return handleEndCallAPI(retries - 1);
        }
        throw error;
      }
    },
    [call?.cid, user.token, dispatch]
  );

  const endCallForEveryone = useCallback(async () => {
    setLoader(true);
    try {
      await call?.endCall();
      await handleEndCallAPI();
      handleRecordingStopped();
      navigate("/dashboard/courses");
      showToast(
        "Session Ended",
        "Call terminated for all participants",
        "info"
      );
    } catch (error) {
      showToast("Error", "Failed to end call. Please try again.", "error");
    } finally {
      setLoader(false);
    }
  }, [call, handleEndCallAPI, navigate, handleRecordingStopped]);

  const confirmEndCall = useCallback(() => {
    confirmDialog
      .fire({
        title: "End Call for Everyone?",
        text: "Are you sure you want to end the call for all participants?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "End Call",
        cancelButtonText: "Cancel",
      })
      .then((result) => {
        if (result.isConfirmed) {
          endCallForEveryone();
        }
      });
  }, [endCallForEveryone]);

  const handleLeaveCall = useCallback(async () => {
    setLoader(true);
    try {
      if (call?.isCreatedByMe) {
        confirmEndCall();
        return;
      }

      confirmDialog
        .fire({
          title: "Leave Call?",
          text: "Are you sure you want to leave the call?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Leave",
          cancelButtonText: "Stay",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            await call?.leave();
            navigate("/dashboard/courses");
            showToast("Left Session", "You left the live session", "success");
          }
        });
    } catch (error) {
      console.error("Leave call error:", error);
      showToast("Error", "Failed to leave call", "error");
    } finally {
      setLoader(false);
    }
  }, [call, navigate, confirmEndCall]);

  useEffect(() => {
    if (!call) return;

    const handleGlobalCallEnd = async () => {
      await Promise.all([
        call.camera?.disable(),
        call.microphone?.disable(),
        call.screenShare?.disable(),
      ]);

      if (call.isCreatedByMe) {
        await handleEndCallAPI();
        handleRecordingStopped();
      }

      navigate("/dashboard/courses");
    };

    call.on("call.ended", handleGlobalCallEnd);
    return () => {
      call.off("call.ended", handleGlobalCallEnd);
    };
  }, [call, navigate, handleEndCallAPI, handleRecordingStopped]);

  // Media control handlers
  const toggleSpeakerMute = useCallback(() => {
    speaker?.state.setVolume(isMuted ? 1 : 0);
    setIsMuted(!isMuted);
  }, [speaker, isMuted]);

  const toggleRaisehandhandler = useCallback(async () => {
    const newState = !toggleRaisehand;
    setToggleRaisehand(newState);

    try {
      if (newState) {
        await call?.sendReaction({
          type: "raised-hand",
          emoji_code: ":raise-hand:",
          custom: { clearAfterTimeout: true },
        });
      } else {
        await call?.sendReaction({ type: "::" });
      }
    } catch (error) {
      console.error("Raise hand error:", error);
    }
  }, [toggleRaisehand, call]);

  const handleScreenShare = async () => {
    try {
      if (screenShare.state.status === "enabled") {
        await call?.screenShare.disable();
      } else {
        call?.screenShare.setSettings({
          maxFramerate: 15,
          maxBitrate: 1500000,
        });
        await call?.screenShare.enable();
      }
    } catch (error) {
      console.error("Screen share error:", error);
      showToast("Error", "Failed to toggle screen sharing", "error");
    }
  };

  // const toggleSpeakerMute = () => {
  //   if (speaker) {
  //     speaker.state.setVolume(isMuted ? 1 : 0);
  //     setIsMuted(!isMuted);
  //   }
  // };

  // const showConfirmDialog = (
  //   title: string,
  //   text: string,
  //   icon: SweetAlertIcon = "warning",
  //   onConfirm: (isConfirmed: boolean) => void
  // ) => {
  //   confirmDialog
  //     .fire({
  //       title: title,
  //       text: text,
  //       icon: icon,
  //       showCancelButton: true, // Enables "Cancel" button
  //       confirmButtonText: "Yes",
  //       cancelButtonText: "No",
  //     })
  //     .then((result) => {
  //       // Call the onConfirm callback with the result of the confirmation
  //       onConfirm(result.isConfirmed);
  //     });
  // };

  // const handleEndCallAPI = useCallback(async () => {
  //   const { data } = await axios.put(
  //     `${API.Courses.app_courses_live_endLive}/${call?.cid}`,
  //     {
  //       notes: "Call ended",
  //     },
  //     {
  //       headers: {
  //         Authorization: `Bearer ${user.token}`,
  //       },
  //     }
  //   );

  //   if (data.status === 200 && data.errorCode === 0) {
  //     dispatch(clearLive());
  //     // showToast("Live", "Live Session has been ended.");
  //   } else {
  //     showToast("Something wrong", data.message, "error");
  //   }
  // }, []);

  // const handleCallEnd = async () => {
  //   await handleEndCallAPI();
  //   navigate("/dashboard/courses");
  //   showToast(
  //     "Session Terminated",
  //     "Session has been ended by the instructor.",
  //     "info"
  //   );
  // };

  // const handleRaiseHand = async () => {
  //   await call?.sendReaction({
  //     type: "raised-hand",
  //     emoji_code: ":raise-hand:",
  //     custom: { clearAfterTimeout: true }, // Disable auto-clear
  //   });
  // };

  // const handleClearRaiseHand = () => {
  //   if (localParticipant) {
  //     call?.sendReaction({
  //       type: "::",
  //     }); // Clear reaction for local participant
  //   }
  // };

  // const toggleRaisehandhandler = () => {
  //   const newState = !toggleRaisehand;
  //   setToggleRaisehand(newState);
  //   if (newState) {
  //     handleRaiseHand();
  //   } else {
  //     handleClearRaiseHand();
  //   }
  // };

  // const handleLeaveCall = useCallback(async () => {
  //   setLoader(true);
  //   try {
  //     if (call?.isCreatedByMe) {
  //       await call.endCall();
  //       handleRecordingStopped();
  //       handleCallEnd();
  //     } else {
  //       if (call?.state.endedAt) {
  //         navigate("/dashboard/courses");
  //         showToast(
  //           "Session Terminated",
  //           "Session has been ended by the instructor.",
  //           "info"
  //         );
  //         return;
  //       }
  //       // Participant chooses to leave voluntarily
  //       showConfirmDialog(
  //         "Are you sure?",
  //         "The live session hasn't finished yet. Do you really want to leave?",
  //         "warning",
  //         async (isConfirmed: boolean) => {
  //           if (isConfirmed) {
  //             await call?.leave();
  //             navigate("/dashboard/courses");
  //             showToast(
  //               "Left the session",
  //               "You have successfully left the live session.",
  //               "success"
  //             );
  //           }
  //         }
  //       );
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     setLoader(false);
  //   }
  // }, [call, navigate, showConfirmDialog, handleCallEnd]);

  // useEffect(() => {
  //   if (!call || !client) return;

  //   const handleDisableMedia = async () => {
  //     await Promise.all([
  //       call?.camera?.disable(),
  //       call?.microphone?.disable(),
  //       call?.screenShare?.disable(),
  //     ]);
  //   };

  //   const handleCallEnded = async () => {
  //     await handleDisableMedia();
  //     if (!call.state.endedAt) return; // Add this check
  //     await handleLeaveCall(); // Ensure this uses the latest version
  //   };

  //   // client.on("call.session_participant_left", handleParticipantLeft);
  //   client.on("call.ended", handleCallEnded);

  //   return () => {
  //     // client.off("call.session_participant_left", handleParticipantLeft);
  //     client.off("call.ended", handleCallEnded);
  //     // clearTimeout(disconnectTimeout);
  //   };
  // }, [call, client, localParticipant, handleLeaveCall]);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 sl:gap-4 ll:gap-6 bg-white rounded-lg p-2 sl:p-4">
      {displayButtons === 3 ? (
        <>
          {/* Mobile-optimized layout */}
          <div className="flex items-center gap-2 sl:gap-4">
            {/* Leave Call Button */}
            <button
              onClick={handleLeaveCall}
              className="p-2 sl:p-3 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
            >
              <img
                src={LeaveCall}
                alt="Leave call"
                className="w-5 h-5 sl:w-6 sl:h-6"
              />
            </button>

            {/* Toggle Microphone Button */}
            <button
              onClick={() => microphone.toggle()}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                isAudioDisabled
                  ? "bg-gray-100 hover:bg-gray-200"
                  : "bg-blue-100 hover:bg-blue-200"
              }`}
            >
              <img
                src={ToggleMic}
                alt="Toggle microphone"
                className="w-5 h-5 sl:w-6 sl:h-6"
                style={{ opacity: isAudioDisabled ? 0.5 : 1 }}
              />
            </button>

            {/* Raise Hand Button */}
            <button
              onClick={toggleRaisehandhandler}
              className="p-2 sl:p-3 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
            >
              <img
                src={RaisHand}
                alt="Raise hand"
                className="w-5 h-5 sl:w-6 sl:h-6"
              />
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Main Controls Grid */}
          <div className="grid grid-flow-col auto-cols-max gap-2 sl:gap-4 ll:gap-6">
            {/* Leave Call Button */}
            <button
              onClick={handleLeaveCall}
              className="p-2 sl:p-3 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
            >
              {loader ? (
                <Loader />
              ) : (
                <img
                  src={LeaveCall}
                  alt="Leave call"
                  className="w-4 h-4 sl:w-5 sl:h-5"
                />
              )}
            </button>

            {/* Screen Share Button */}
            <button
              disabled={!isScreenSharing && isSomeoneScreenSharing}
              onClick={handleScreenShare}
              // onClick={() => {
              //   call?.screenShare.setSettings({
              //     maxFramerate: 15,
              //     maxBitrate: 1500000,
              //   });
              //   screenShare.toggle();
              // }}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                isScreenSharing
                  ? "bg-blue-100 hover:bg-blue-200"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              <img
                src={ScreenShare}
                alt="Screen share"
                className="w-4 h-4 sl:w-5 sl:h-5"
                style={{ opacity: isScreenSharing ? 1 : 0.5 }}
              />
            </button>

            {/* Toggle Camera Button */}
            <button
              onClick={() => camera.toggle()}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                isCamDisabled
                  ? "bg-gray-100 hover:bg-gray-200"
                  : "bg-blue-100 hover:bg-blue-200"
              }`}
            >
              <img
                src={ToggleCam}
                alt="Toggle camera"
                className="w-4 h-4 sl:w-5 sl:h-5"
                style={{ opacity: isCamDisabled ? 0.5 : 1 }}
              />
            </button>

            {/* Toggle Microphone Button */}
            <button
              onClick={() => microphone.toggle()}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                isAudioDisabled
                  ? "bg-gray-100 hover:bg-gray-200"
                  : "bg-blue-100 hover:bg-blue-200"
              }`}
            >
              <img
                src={ToggleMic}
                alt="Toggle microphone"
                className="w-4 h-4 sl:w-5 sl:h-5"
                style={{ opacity: isAudioDisabled ? 0.5 : 1 }}
              />
            </button>
          </div>

          {/* Secondary Controls */}
          <div className="grid grid-flow-col auto-cols-max gap-2 sl:gap-4 ll:gap-6">
            {/* Mute Speakers Button */}
            <button
              onClick={toggleSpeakerMute}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                isMuted
                  ? "bg-red-100 hover:bg-red-200"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              <img
                src={MuteSpeakers}
                alt="Mute speakers"
                className="w-4 h-4 sl:w-5 sl:h-5"
                style={{ opacity: isMuted ? 0.5 : 1 }}
              />
            </button>

            {/* Connection Status Button */}
            <button className="p-2 sl:p-3 rounded-full bg-gray-100" disabled>
              <ConnectionQualityIndicator
                quality={localParticipant?.connectionQuality}
                width={"20px"}
                height={"20px"}
              />
            </button>

            <button
              onClick={toggleRaisehandhandler}
              className={`p-2 sl:p-3 rounded-full transition-colors ${
                toggleRaisehand ? "bg-blue-300" : "bg-blue-100"
              } hover:bg-blue-200`}
            >
              <img
                src={RaisHand}
                alt="Raise hand"
                className="w-4 h-4 sl:w-5 sl:h-5"
                style={{ opacity: toggleRaisehand ? 1 : 0.5 }}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
  // return (
  //   <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 lg:gap-6 bg-white rounded-lg p-2 md:p-4">
  //     {displayButtons === 3 ? (
  //       <>
  //         {/* Mobile-optimized layout */}
  //         <div className="flex items-center gap-2 md:gap-4">
  //           {/* Leave Call Button */}
  //           <button
  //             onClick={handleLeaveCall}
  //             className="p-2 md:p-3 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
  //           >
  //             <img
  //               src={LeaveCall}
  //               alt="Leave call"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //             />
  //           </button>

  //           {/* Toggle Microphone Button */}
  //           <button
  //             onClick={() => microphone.toggle()}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               isAudioDisabled
  //                 ? "bg-gray-100 hover:bg-gray-200"
  //                 : "bg-blue-100 hover:bg-blue-200"
  //             }`}
  //           >
  //             <img
  //               src={ToggleMic}
  //               alt="Toggle microphone"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: isAudioDisabled ? 0.5 : 1 }}
  //             />
  //           </button>

  //           {/* Raise Hand Button */}
  //           <button
  //             onClick={toggleRaisehandhandler}
  //             className="p-2 md:p-3 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
  //           >
  //             <img
  //               src={RaisHand}
  //               alt="Raise hand"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //             />
  //           </button>
  //         </div>
  //       </>
  //     ) : (
  //       <>
  //         {/* Main Controls Grid */}
  //         <div className="grid grid-flow-col auto-cols-max gap-2 md:gap-4 lg:gap-6">
  //           {/* Leave Call Button */}
  //           <button
  //             onClick={handleLeaveCall}
  //             className="p-2 md:p-3 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
  //           >
  //             {loader ? (
  //               <Loader />
  //             ) : (
  //               <img
  //                 src={LeaveCall}
  //                 alt="Leave call"
  //                 className="w-5 h-5 md:w-6 md:h-6"
  //               />
  //             )}
  //           </button>

  //           {/* Screen Share Button */}
  //           <button
  //             disabled={!isScreenSharing && isSomeoneScreenSharing}
  //             onClick={() => {
  //               call?.screenShare.setSettings({
  //                 maxFramerate: 20,
  //                 maxBitrate: 1500000,
  //               });
  //               screenShare.toggle();
  //             }}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               isScreenSharing
  //                 ? "bg-blue-100 hover:bg-blue-200"
  //                 : "bg-gray-100 hover:bg-gray-200"
  //             }`}
  //           >
  //             <img
  //               src={ScreenShare}
  //               alt="Screen share"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: isScreenSharing ? 1 : 0.5 }}
  //             />
  //           </button>

  //           {/* Toggle Camera Button */}
  //           <button
  //             onClick={() => camera.toggle()}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               isCamDisabled
  //                 ? "bg-gray-100 hover:bg-gray-200"
  //                 : "bg-blue-100 hover:bg-blue-200"
  //             }`}
  //           >
  //             <img
  //               src={ToggleCam}
  //               alt="Toggle camera"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: isCamDisabled ? 0.5 : 1 }}
  //             />
  //           </button>

  //           {/* Toggle Microphone Button */}
  //           <button
  //             onClick={() => microphone.toggle()}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               isAudioDisabled
  //                 ? "bg-gray-100 hover:bg-gray-200"
  //                 : "bg-blue-100 hover:bg-blue-200"
  //             }`}
  //           >
  //             <img
  //               src={ToggleMic}
  //               alt="Toggle microphone"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: isAudioDisabled ? 0.5 : 1 }}
  //             />
  //           </button>
  //         </div>

  //         {/* Secondary Controls */}
  //         <div className="grid grid-flow-col auto-cols-max gap-2 md:gap-4 lg:gap-6">
  //           {/* Mute Speakers Button */}
  //           <button
  //             onClick={toggleSpeakerMute}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               isMuted
  //                 ? "bg-red-100 hover:bg-red-200"
  //                 : "bg-gray-100 hover:bg-gray-200"
  //             }`}
  //           >
  //             <img
  //               src={MuteSpeakers}
  //               alt="Mute speakers"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: isMuted ? 0.5 : 1 }}
  //             />
  //           </button>

  //           {/* Connection Status Button */}
  //           <button className="p-2 md:p-3 rounded-full bg-gray-100" disabled>
  //             <ConnectionQualityIndicator
  //               quality={localParticipant?.connectionQuality}
  //             />
  //           </button>

  //           <button
  //             onClick={toggleRaisehandhandler}
  //             className={`p-2 md:p-3 rounded-full transition-colors ${
  //               toggleRaisehand ? "bg-blue-300" : "bg-blue-100"
  //             } hover:bg-blue-200`}
  //           >
  //             <img
  //               src={RaisHand}
  //               alt="Raise hand"
  //               className="w-5 h-5 md:w-6 md:h-6"
  //               style={{ opacity: toggleRaisehand ? 1 : 0.5 }}
  //             />
  //           </button>
  //         </div>
  //       </>
  //     )}
  //   </div>
  // );
};

export default React.memo(CallControls);
